/**
 * Labstep
 *
 * @module core/Location
 * @desc A location aware (dummy) component
 * Use this to fire any actions you want on location change
 *
 * TODO: Move away from this dummy component. Instead user history service to
 * fire events
 */

import { menuName as leftMenuName } from 'labstep-web/components/Layout/Menu/Left';
import { withActiveEditModal } from 'labstep-web/containers/ActiveEditModal';
import { withSidebar } from 'labstep-web/containers/Ui/Sidebar';
import bugsnagService from 'labstep-web/services/bugsnag.service';
import store from 'labstep-web/state/store';
import React from 'react';
import { withRouter } from 'react-router-dom';
import type { ILocationProps } from './types';

export const isLocationTheSame = (prev: any, next: any): boolean =>
  prev.pathname === next.pathname && prev.search === next.search;

// Check if the only difference between two locations is the modal route param (m)
export const isModalRouteChange = (prev: any, next: any): boolean => {
  if (prev.pathname !== next.pathname) {
    return false;
  }

  const prevParams = new URLSearchParams(prev.search);
  const nextParams = new URLSearchParams(next.search);

  const prevM = prevParams.getAll('m');
  const nextM = nextParams.getAll('m');

  prevParams.delete('m');
  nextParams.delete('m');

  // Check if all non-m params are the same
  return (
    prevParams.toString() === nextParams.toString() &&
    (prevM.length !== nextM.length ||
      !prevM.every((val, idx) => val === nextM[idx]))
  );
};

const eventName = 'locationChange';

export class Location extends React.Component<ILocationProps> {
  componentDidMount() {
    const { location } = this.props;
    const path = `${location.pathname}${location.search}`;

    store.dispatch({
      type: 'LOCATION_CHANGE',
      meta: {
        path,
      },
    });
  }

  componentDidUpdate(prevProps: ILocationProps) {
    const {
      location,
      history: { action },
      setSidebar,
      activeEditModal,
      setActiveEditModal,
    } = this.props;

    if (location.key !== prevProps.location.key) {
      setSidebar(false, leftMenuName);
    }

    if (isLocationTheSame(prevProps.location, location)) {
      return;
    }

    // Don't clear activeEditModal if the only change is opening/closing a modal route
    if (
      activeEditModal &&
      !isModalRouteChange(prevProps.location, location)
    ) {
      setActiveEditModal(null);
    }

    if (action === 'PUSH') {
      // On navigation to new page - scroll to top
      window.scrollTo(0, 0);
      const event = new Event(eventName);
      window.dispatchEvent(event);
    }
    const path = `${location.pathname}${location.search}`;
    bugsnagService.leaveBreadcrumb('Route changed', { path });

    store.dispatch({
      type: 'LOCATION_CHANGE',
      meta: {
        path,
      },
    });
  }

  render() {
    return <span />;
  }
}

// Create a new component that is "connected" (to borrow redux
// terminology) to the router.
export default withRouter(withSidebar(withActiveEditModal(Location)));
